/* eslint-disable i18next/no-literal-string */
// src/components/errors/ErrorFilters.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Autocomplete,
  Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  Clear as ClearIcon,
} from '@mui/icons-material';

export default function ErrorFilters({
  show,
  searchQuery,
  selectedDepartment,
  selectedStaffMember,
  selectedEndpoint,
  selectedErrorCode,
  startDate,
  endDate,
  departments = [],
  staff = [],
  errorCodes = [],
  endpoints = [],
  currentView,
  onSearchChange,
  onDepartmentChange,
  onStaffMemberChange,
  onEndpointChange,
  onErrorCodeChange,
  onStartDateChange,
  onEndDateChange,
  onClearFilters,
}) {
  if (!show) return null;

  const filteredStaff = selectedDepartment
    ? staff.filter(s => s.department_id === selectedDepartment)
    : staff;

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6">
              Filters
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={onClearFilters}
            >
              Clear All
            </Button>
          </Box>

          {/* Main filters container with flexbox */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            {/* Search field - takes more space */}
            <Box flex="2 1 300px" minWidth="250px">
              <TextField
                fullWidth
                size="small"
                label="Search Errors"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by message, code, or endpoint..."
              />
            </Box>

            {/* Department filter */}
            <Box flex="1 1 200px" minWidth="180px">
              <FormControl fullWidth size="small">
                <InputLabel id="error-filters-select-label">Department</InputLabel>
                <Select labelId="error-filters-select-label"
                  value={selectedDepartment}
                  label="Department"
                  onChange={(e) => onDepartmentChange(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.department_id} value={dept.department_id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Error Code filter */}
            <Box flex="1 1 200px" minWidth="180px">
              <Autocomplete
                options={errorCodes}
                value={selectedErrorCode}
                onChange={(event, value) => onErrorCodeChange(value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Error Code"
                    size="small"
                    placeholder="Select error code..."
                  />
                )}
              />
            </Box>

            {/* Staff Member Filter - only show when relevant */}
            {(currentView === 'by-staff' || currentView === 'overview') && (
              <Box flex="2 1 280px" minWidth="250px">
                <Autocomplete
                  options={filteredStaff}
                  getOptionLabel={(option) => `${option.name} (${option.department_name || 'No Dept'})`}
                  value={filteredStaff.find(s => s.staff_member_id === selectedStaffMember) || null}
                  onChange={(event, value) => onStaffMemberChange(value?.staff_member_id || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Staff Member"
                      size="small"
                      placeholder="Select staff member..."
                    />
                  )}
                  renderOption={({ key, ...props }, option) => (
                    <Box key={key} component="li" {...props}>
                      <Box>
                        <Typography variant="body2">
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.department_name || 'No Department'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Box>
            )}

            {/* Endpoint Filter - only show when relevant */}
            {(currentView === 'by-endpoint' || currentView === 'overview') && (
              <Box flex="2 1 280px" minWidth="250px">
                <Autocomplete
                  options={endpoints}
                  value={selectedEndpoint}
                  onChange={(event, value) => onEndpointChange(value || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Endpoint"
                      size="small"
                      placeholder="Select endpoint..."
                    />
                  )}
                />
              </Box>
            )}

            {/* Start Date */}
            <Box flex="1 1 220px" minWidth="200px">
              <DateTimePicker
                label="Start Date"
                value={startDate}
                onChange={onStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Box>

            {/* End Date */}
            <Box flex="1 1 220px" minWidth="200px">
              <DateTimePicker
                label="End Date"
                value={endDate}
                onChange={onEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
                minDateTime={startDate}
              />
            </Box>
          </Box>

          {/* Active Filters Summary */}
          {(selectedDepartment || selectedStaffMember || selectedEndpoint || selectedErrorCode || (startDate && endDate) || searchQuery) && (
            <Box pt={2} borderTop={1} borderColor="divider">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Active filters:
              </Typography>
              <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                {selectedDepartment && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Department: {departments.find(d => d.department_id === selectedDepartment)?.name}
                  </Typography>
                )}
                {selectedStaffMember && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'secondary.main', 
                    color: 'secondary.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Staff: {filteredStaff.find(s => s.staff_member_id === selectedStaffMember)?.name}
                  </Typography>
                )}
                {selectedEndpoint && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'info.main', 
                    color: 'info.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Endpoint: {selectedEndpoint}
                  </Typography>
                )}
                {selectedErrorCode && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'error.main', 
                    color: 'error.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Code: {selectedErrorCode}
                  </Typography>
                )}
                {startDate && endDate && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'warning.main', 
                    color: 'warning.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Date Range: {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                  </Typography>
                )}
                {searchQuery && (
                  <Typography variant="caption" sx={{ 
                    px: 1.5, py: 0.75, 
                    backgroundColor: 'success.main', 
                    color: 'success.contrastText', 
                    borderRadius: 1.5,
                    fontWeight: 500
                  }}>
                    Search: "{searchQuery}"
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
}